import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import type { BundleItem } from './bundle';

/**
 * Normalizes a `BundleItem.product` value (which may be a single entry or an
 * array) to a single `xgritProductId`, mirroring `ProductService.extractProduct`
 * (`[0]` when array). Returns `null` when no usable id is present.
 */
function extractProductId(
  product: CraftXGritProductEntry | CraftXGritProductEntry[] | null | undefined,
): string | null {
  if (!product) {
    return null;
  }
  const single = Array.isArray(product) ? product[0] : product;
  const id = single?.xgritProductId;
  return id && id.length > 0 ? id : null;
}

/**
 * Builds a Schema.org ItemList node referencing each bundle's course by URN.
 *
 * The page-data resolver auto-registers the Course node for each
 * `xgritProductId` (`urn:xgrit:course:{id}`), so this fragment only emits the
 * ItemList wrapper and cross-references those Courses by `@id`.
 *
 * Returns `null` when the input is empty or no item resolves to a valid id.
 */
export function getBundleSchema(bundles: BundleItem[] | null | undefined, pageUrl: string): SchemaOrgNode | null {
  if (!bundles || bundles.length === 0) {
    return null;
  }

  const seen = new Set<string>();
  const courseIds: string[] = [];
  bundles.forEach((bundle) => {
    const id = extractProductId(bundle?.product);
    if (id && !seen.has(id)) {
      seen.add(id);
      courseIds.push(id);
    }
  });

  if (courseIds.length === 0) {
    return null;
  }

  const itemListElement = courseIds.map((id, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: { '@id': `urn:xgrit:course:${id}` },
  }));

  return {
    '@type': 'ItemList',
    '@id': `${canonicalIdBase(pageUrl)}#bundle`,
    numberOfItems: courseIds.length,
    itemListElement,
  };
}
